 
import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { logActivity } from "@/services/activities";

interface CloseSessionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: any;
}

export default function CloseSessionModal({
  open,
  onOpenChange,
  session,
}: CloseSessionModalProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [breakdowns, setBreakdowns] = useState<any[]>([]);
  const [accountRows, setAccountRows] = useState<any[]>([]);
  const [stepIndex, setStepIndex] = useState(0); // 0=intro, 1..N=contas, N+1=resumo

  // Calcular saldo esperado e breakdowns
  const { data: expectedData, refetch: refetchExpected, isLoading: loadingExpected } = useQuery({
    queryKey: ["session-expected", session?.id],
    queryFn: async () => {
      try {
      // Data de fechamento (agora ou a data da sessão se já estiver fechada)
      const closingDate = session.closed_at ? new Date(session.closed_at) : new Date();
      const closingDateIso = closingDate.toISOString();
      const openingDateIso = session.opened_at;

      // Buscar pagamentos: transações vinculadas à sessão E transações sem sessão entre abertura e fechamento
      let paymentsData: any[] = [];
      
      // 1. Pagamentos de transações já vinculadas à sessão (filtrar por data)
      const { data: paysBySession, error: paysBySessErr } = await supabase
        .from('transaction_payments')
        .select('id, created_at, confirmed_at, status, valor, net_amount, data_prevista, bank_account_id, payment_method_id, tenant_id, transaction_id, transactions!inner(id, created_at, tenant_id, cash_register_session_id, client_id, categoria, descricao)')
        .eq('transactions.cash_register_session_id', session.id)
        .gte('transactions.created_at', openingDateIso)
        .lte('transactions.created_at', closingDateIso);
      if (paysBySessErr) throw paysBySessErr;
      
      // 2. Pagamentos de transações sem sessão entre abertura e fechamento
      const { data: paysNoSession, error: paysNoSessErr } = await supabase
        .from('transaction_payments')
        .select('id, created_at, confirmed_at, status, valor, net_amount, data_prevista, bank_account_id, payment_method_id, tenant_id, transaction_id, transactions!inner(id, created_at, tenant_id, cash_register_session_id, client_id, categoria, descricao)')
        .eq('transactions.tenant_id', session.tenant_id)
        .is('transactions.cash_register_session_id', null)
        .gte('transactions.created_at', openingDateIso)
        .lte('transactions.created_at', closingDateIso);
      if (paysNoSessErr) throw paysNoSessErr;
      
      // Combinar resultados
      paymentsData = [...(paysBySession || []), ...(paysNoSession || [])];

      // Agregar por bank_account_id × payment_method_id (pagamentos confirmados)
      const breakdownMap = new Map<string, any>();
      const paymentRows: Array<{ id?: string; transaction_id?: string; created_at: string; bank_account_id: string | null; payment_method_id: string | null; valor: number; net_amount?: number; data_prevista?: string | null; status: string; tx_client_id?: string | null; tx_category?: string | null; tx_description?: string | null }>= [];
      (paymentsData || []).forEach((r: any) => {
        const status = (r?.status || 'pendente') as string;
        const bankId = r?.bank_account_id || null;
        const methodId = r?.payment_method_id || null;
        const valor = Number(r?.valor) || 0;
        const createdAt = r?.confirmed_at || r?.created_at || r?.transactions?.created_at;
        paymentRows.push({ id: r?.id, transaction_id: r?.transaction_id, created_at: createdAt, bank_account_id: bankId, payment_method_id: methodId, valor, net_amount: r?.net_amount ?? undefined, data_prevista: r?.data_prevista ?? null, status, tx_client_id: r?.transactions?.client_id ?? null, tx_category: r?.transactions?.categoria ?? null, tx_description: r?.transactions?.descricao ?? null });

        if (status !== 'confirmado' || !bankId) return;
        const key = `${bankId}-${methodId}`;
        // Usar net_amount (valor líquido) em vez de valor bruto
        const netValue = typeof r.net_amount === "number" ? Number(r.net_amount) : Number(r.valor) || 0;
        if (!breakdownMap.has(key)) {
          breakdownMap.set(key, {
            bank_account_id: bankId,
            payment_method_id: methodId,
            expected_amount: 0,
          });
        }
        const breakdown = breakdownMap.get(key);
        breakdown.expected_amount += netValue;
      });

      // Buscar despesas: transações vinculadas à sessão E transações sem sessão entre abertura e fechamento
      let expensesTx: any[] = [];
      
      // 1. Despesas de transações já vinculadas à sessão (filtrar por data)
      const { data: expBySession, error: expBySessErr } = await supabase
        .from('transactions')
        .select('id, created_at, valor_liquido, bank_account_id, payment_method_id, tipo, categoria, descricao')
        .eq('cash_register_session_id', session.id)
        .eq('tipo', 'saida')
        .gte('created_at', openingDateIso)
        .lte('created_at', closingDateIso);
      if (expBySessErr) throw expBySessErr;
      
      // 2. Despesas de transações sem sessão entre abertura e fechamento
      const { data: expNoSession, error: expNoSessErr } = await supabase
        .from('transactions')
        .select('id, created_at, valor_liquido, bank_account_id, payment_method_id, tipo, categoria, descricao')
        .eq('tenant_id', session.tenant_id)
        .eq('tipo', 'saida')
        .is('cash_register_session_id', null)
        .gte('created_at', openingDateIso)
        .lte('created_at', closingDateIso);
      if (expNoSessErr) throw expNoSessErr;
      
      // Combinar resultados
      expensesTx = [...(expBySession || []), ...(expNoSession || [])];

      // Preparar array base de breakdowns e coletar IDs para batch fetch
      const breakdownsBase = Array.from(breakdownMap.values());

      // Buscar snapshots de abertura por conta (phase='opening')
      let { data: openings, error: openErr } = await supabase
        .from("cash_register_breakdown")
        .select("bank_account_id, declared_amount")
        .eq("session_id", session.id)
        .eq("phase", "opening");
      if (openErr) throw openErr;
      // Fallback: se não houver linhas opening, considerar linhas sem método como abertura
      if (!openings || openings.length === 0) {
        const { data: openingsAlt, error: openAltErr } = await supabase
          .from("cash_register_breakdown")
          .select("bank_account_id, declared_amount")
          .eq("session_id", session.id)
          .is("payment_method_id", null);
        if (!openAltErr && openingsAlt && openingsAlt.length > 0) {
          openings = openingsAlt;
        }
      }

      // Calcular esperado por conta: opening + soma dos movimentos da conta
      const movementPerAccount = new Map<string, number>();
      breakdownsBase.forEach((b: any) => {
        const current = movementPerAccount.get(b.bank_account_id) || 0;
        movementPerAccount.set(b.bank_account_id, current + (Number(b.expected_amount) || 0));
      });

      let accountIds = Array.from(
        new Set([...(openings?.map((o: any) => o.bank_account_id) || []), ...Array.from(movementPerAccount.keys())])
      );

      // Fallback: se não houver nenhuma conta nas aberturas/movimentos, buscar todas as contas ativas do tenant
      if (accountIds.length === 0) {
        const { data: activeAccounts, error: accErr } = await supabase
          .from("bank_accounts")
          .select("id, nome")
          .eq("tenant_id", session.tenant_id)
          .eq("status", "ativo");
        if (accErr) throw accErr;
        accountIds = activeAccounts?.map((a: any) => a.id) || [];
      }

      let { data: accountsData } = await supabase
        .from("bank_accounts")
        .select("id, nome")
        .eq("tenant_id", session.tenant_id)
        .in("id", accountIds.length ? accountIds : ["00000000-0000-0000-0000-000000000000"]);

      if (!accountsData || accountsData.length === 0) {
        // Fallback: buscar todas as contas ativas do tenant
        const { data: allTenantAccounts } = await supabase
          .from("bank_accounts")
          .select("id, nome")
          .eq("tenant_id", session.tenant_id)
          .eq("status", "ativo");
        accountsData = allTenantAccounts || [];
        if (accountIds.length === 0) {
          accountIds = accountsData.map((a: any) => a.id);
        }
      }

      const nameById = new Map<string, string>();
      (accountsData || []).forEach((a: any) => nameById.set(a.id, a.nome));

      const accountRows = accountIds.map((id) => {
        const opening = Number(openings?.find((o: any) => o.bank_account_id === id)?.declared_amount) || 0;
        const movement = movementPerAccount.get(id) || 0;
        const expected = opening + movement;
        return {
          bank_account_id: id,
          bank_account_name: nameById.get(id) || "Desconhecido",
          expected_amount: expected,
          declared_amount: expected,
          notes: "",
        };
      });

      const expectedBalance = (accountRows.length ? accountRows : []).reduce((sum, r) => sum + (r.expected_amount || 0), 0);

      // Enriquecer nomes de conta e método para listagem (com base nos pagamentos e despesas)
      const expAccIds = expensesTx.map((t:any) => t.bank_account_id).filter(Boolean) as string[];
      const expPmIds = expensesTx.map((t:any) => t.payment_method_id).filter(Boolean) as string[];
      const accIds = Array.from(new Set([...
        (paymentRows.map((r) => r.bank_account_id).filter(Boolean) as string[]),
        ...expAccIds,
      ]));
      const pmIds = Array.from(new Set([...
        (paymentRows.map((r) => r.payment_method_id).filter(Boolean) as string[]),
        ...expPmIds,
      ]));

      const [{ data: accs }, { data: pms }] = await Promise.all([
        accIds.length
          ? supabase.from("bank_accounts").select("id,nome").eq("tenant_id", session.tenant_id).in("id", accIds)
          : Promise.resolve({ data: [] as any[] } as any),
        pmIds.length
          ? supabase.from("payment_methods").select("id,nome,slug,instant_settlement").in("id", pmIds)
          : Promise.resolve({ data: [] as any[] } as any),
      ]);
      const accNameById = new Map<string, string>();
      const pmNameById = new Map<string, string>();
      (accs || []).forEach((a:any) => accNameById.set(a.id, a.nome));
      (pms || []).forEach((m:any) => pmNameById.set(m.id, m.nome));

      // Mapear métodos instantâneos
      const isInstantByPm = new Map<string, boolean>();
      (pms || []).forEach((m:any) => {
        let instant = m.instant_settlement;
        if (instant === null || instant === undefined) {
          const slug = (m.slug || '').toLowerCase();
          if (slug.includes('credito')) instant = false;
          else if (slug.includes('pix') || slug.includes('debito') || slug.includes('dinheiro') || slug.includes('transfer')) instant = true;
        }
        isInstantByPm.set(m.id, !!instant);
      });

      // Recalcular esperado por conta: abertura + entradas LIQUIDADAS (net_amount) - saídas
      const openingByAcc = new Map<string, number>();
      (openings || []).forEach((o:any) => {
        openingByAcc.set(o.bank_account_id, Number(o.declared_amount) || 0);
      });

      const incomeInstantByAcc = new Map<string, number>();
      const incomeLiquidatedByAcc = new Map<string, number>();
      (paymentRows || []).forEach((r:any) => {
        const accId = r.bank_account_id as string | null;
        if (!accId) return;

        const status = (r.status || 'pendente') as string;
        if (status !== 'confirmado') return;

        // Verificar se já liquidou (data_prevista <= data de fechamento)
        const dataPrevista = r.data_prevista ? new Date(r.data_prevista) : null;
        const isLiquidated = !dataPrevista || dataPrevista <= closingDate;

        if (isLiquidated) {
          // Usar net_amount (já com taxa descontada)
          const netValue = Number(r.net_amount) || Number(r.valor) || 0;
          incomeLiquidatedByAcc.set(accId, (incomeLiquidatedByAcc.get(accId) || 0) + netValue);

          const pmId = r.payment_method_id as string | null;
          if (pmId && isInstantByPm.get(pmId) === true) {
            incomeInstantByAcc.set(accId, (incomeInstantByAcc.get(accId) || 0) + netValue);
          }
        }
      });
      const expenseByAcc = new Map<string, number>();
      (expensesTx || []).forEach((t:any) => {
        const accId = t.bank_account_id as string | null;
        if (!accId) return;
        const prev = expenseByAcc.get(accId) || 0;
        expenseByAcc.set(accId, prev + (Number(t.valor_liquido) || 0));
      });

      const allAccIdsForRows = Array.from(new Set([
        ...Array.from(openingByAcc.keys()),
        ...((paymentRows || []).map((r:any)=>r.bank_account_id).filter(Boolean) as string[]),
        ...((expensesTx || []).map((t:any)=>t.bank_account_id).filter(Boolean) as string[]),
      ]));

      const movementRowsWithNames = allAccIdsForRows.map((accId:string) => {
        const opening = (openingByAcc.get(accId) || 0);
        const liquidatedIncome = incomeLiquidatedByAcc.get(accId) || 0;
        const expenses = expenseByAcc.get(accId) || 0;
        const expectedRaw = opening + liquidatedIncome - expenses;
        const expected = Math.max(0, expectedRaw);
        return {
          bank_account_id: accId,
          bank_account_name: nameById.get(accId) || accNameById.get(accId) || 'Desconhecido',
          opening_amount: opening,
          expected_amount: expected,
          declared_amount: expected,
          notes: '',
        };
      });
      // Enriquecer com nome do cliente e nomes de produtos por transação
      const txIds = Array.from(new Set((paymentRows || []).map(r => r.transaction_id).filter(Boolean))) as string[];
      const clientIds = Array.from(new Set((paymentRows || []).map(r => r.tx_client_id).filter(Boolean))) as string[];
      const clientNameById = new Map<string, string>();
      const productNamesByTx = new Map<string, string>();
      if (txIds.length > 0 || clientIds.length > 0) {
        const [{ data: clientsData }, { data: itemsData }] = await Promise.all([
          clientIds.length ? supabase.from('clients').select('id, nome').in('id', clientIds) : Promise.resolve({ data: [] as any[] } as any),
          txIds.length ? supabase.from('transaction_items').select('transaction_id, products!inner(nome)').in('transaction_id', txIds) : Promise.resolve({ data: [] as any[] } as any),
        ]);
        (clientsData || []).forEach((c:any) => clientNameById.set(c.id, c.nome));
        (itemsData || []).forEach((it:any) => {
          const prev = productNamesByTx.get(it.transaction_id) || '';
          const next = it.products?.nome ? (prev ? `${prev}, ${it.products.nome}` : it.products.nome) : prev;
          productNamesByTx.set(it.transaction_id, next);
        });
      }

      // Lista compacta: entradas a partir de payments + saídas a partir de transactions
      paymentRows.sort((a,b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      const incomeRows = paymentRows.map((r, idx) => ({
        id: `pay-${idx}`,
        payment_id: r.id,
        transaction_id: r.transaction_id,
        created_at: r.created_at,
        bank_account_id: r.bank_account_id,
        payment_method_id: r.payment_method_id,
        bank_account_name: r.bank_account_id ? accNameById.get(r.bank_account_id) || '-' : '-',
        payment_method_name: r.payment_method_id ? pmNameById.get(r.payment_method_id) || '-' : '-',
        valor: Number(r.valor) || 0,
        valor_liquido: Number(r.net_amount ?? r.valor) || 0,
        client_name: r.tx_client_id ? (clientNameById.get(r.tx_client_id) || '-') : undefined,
        product_names: r.transaction_id ? (productNamesByTx.get(r.transaction_id) || undefined) : undefined,
        net_amount: r.net_amount ?? undefined,
        data_prevista: r.data_prevista ?? null,
        tipo: 'entrada' as const,
        status: r.status as string,
        is_instant: r.payment_method_id ? (isInstantByPm.get(r.payment_method_id) === true) : true,
      }));
      const expenseRows = (expensesTx || []).map((t:any, i:number) => ({
        id: `exp-${i}-${t.id}`,
        created_at: t.created_at,
        bank_account_id: t.bank_account_id,
        payment_method_id: t.payment_method_id,
        bank_account_name: t.bank_account_id ? accNameById.get(t.bank_account_id) || '-' : '-',
        payment_method_name: t.payment_method_id ? pmNameById.get(t.payment_method_id) || '-' : '-',
        valor_liquido: Number(t.valor_liquido) || 0,
        categoria: t.categoria || null,
        descricao: t.descricao || null,
        tipo: 'saida' as const,
        status: 'confirmado' as const,
        is_instant: t.payment_method_id ? (isInstantByPm.get(t.payment_method_id) === true) : true,
      }));
      const transactionsWithNames = [...incomeRows, ...expenseRows].sort((a,b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      // Montar breakdowns com nomes (conta × método)
      const breakdownsWithNames = breakdownsBase.map((b:any) => ({
        bank_account_id: b.bank_account_id,
        bank_account_name: nameById.get(b.bank_account_id) || accNameById.get(b.bank_account_id) || 'Desconhecido',
        payment_method_id: b.payment_method_id,
        payment_method_name: b.payment_method_id ? (pmNameById.get(b.payment_method_id) || '-') : '-',
        expected_amount: Number(b.expected_amount) || 0,
        declared_amount: Number(b.expected_amount) || 0,
        notes: '',
      }));

      const expectedBalanceNew = movementRowsWithNames.reduce((sum, r:any) => sum + (r.expected_amount || 0), 0);

      return {
        expected_balance: expectedBalanceNew,
        breakdowns: breakdownsWithNames,
        accounts: movementRowsWithNames,
        transactions: transactionsWithNames,
        payments_raw: incomeRows,
        credit_by_account: (() => {
          const map = new Map<string, number>();
          (paymentRows || []).forEach((r:any) => {
          const pmId = r.payment_method_id as string | null;
          if (pmId && isInstantByPm.get(pmId) === false && (r.status || 'pendente') === 'confirmado') {
            const accId = r.bank_account_id as string | null;
            if (!accId) return;
            // Usar net_amount (valor líquido) em vez de valor bruto
            const netValue = typeof r.net_amount === "number" ? Number(r.net_amount) : Number(r.valor) || 0;
            map.set(accId, (map.get(accId) || 0) + netValue);
          }
          });
          return Array.from(map.entries()).map(([bank_account_id, total]) => ({
            bank_account_id,
            bank_account_name: nameById.get(bank_account_id) || accNameById.get(bank_account_id) || 'Desconhecido',
            total,
          }));
        })(),
      };
      } catch (e) {
        console.error('session-expected error', e);
        // Fallback: tentar pelo menos trazer contas ativas do tenant
        let fallbackAccounts: any[] = [];
        try {
          const { data: allTenantAccounts } = await supabase
            .from('bank_accounts')
            .select('id, nome')
            .eq('tenant_id', session.tenant_id)
            .eq('status', 'ativo');
          fallbackAccounts = (allTenantAccounts || []).map((a: any) => ({
            bank_account_id: a.id,
            bank_account_name: a.nome,
            expected_amount: 0,
            declared_amount: 0,
            notes: '',
          }));
        } catch {}
        return {
          expected_balance: 0,
          breakdowns: [],
          accounts: fallbackAccounts,
          transactions: [],
        };
      }
    },
    enabled: !!session?.id && open,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 30000,
    refetchInterval: false,
  });

  useEffect(() => {
    if (expectedData?.breakdowns) {
      setBreakdowns(expectedData.breakdowns);
    }
    if (expectedData?.accounts) {
      setAccountRows(expectedData.accounts);
    }
  }, [expectedData]);

  // Reajustar step ao abrir e quando mudar quantidade de contas
  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);
  useEffect(() => {
    const total = 1 + (accountRows?.length || 0) + 1;
    setStepIndex((s) => Math.min(Math.max(0, s), Math.max(0, total - 1)));
  }, [accountRows]);

  const totalSteps = 1 /*intro*/ + (accountRows?.length || 0) + 1 /*resumo*/;
  const isIntro = stepIndex === 0;
  const isSummary = stepIndex === totalSteps - 1;
  const currentAccount = !isIntro && !isSummary ? accountRows[stepIndex - 1] : null;

  // Data de referência para o fechamento (agora)
  const closingDate = new Date();

  // Filtrar pagamentos da conta E que já foram liquidados
  const accountPayments = currentAccount
    ? (expectedData?.payments_raw || []).filter((p: any) => {
        if (p.bank_account_id !== currentAccount.bank_account_id) return false;
        
        // Verificar se já liquidou (data_prevista <= hoje)
        const dataPrevista = p.data_prevista ? new Date(p.data_prevista) : null;
        const isLiquidated = !dataPrevista || dataPrevista <= closingDate;
        
        return p.status === 'confirmado' && isLiquidated;
      })
    : [];

  // Pagamentos futuros (ainda não liquidados)
  const futurePayments = currentAccount
    ? (expectedData?.payments_raw || []).filter((p: any) => {
        if (p.bank_account_id !== currentAccount.bank_account_id) return false;
        
        const dataPrevista = p.data_prevista ? new Date(p.data_prevista) : null;
        const isLiquidated = !dataPrevista || dataPrevista <= closingDate;
        
        return p.status === 'confirmado' && !isLiquidated;
      })
    : [];

  // Pagamentos pendentes
  const pendingPayments = currentAccount
    ? (expectedData?.payments_raw || []).filter((p: any) => {
        if (p.bank_account_id !== currentAccount.bank_account_id) return false;
        return p.status === 'pendente';
      })
    : [];

  const paymentsGrouped = accountPayments.reduce((groups, payment) => {
    const key = String(payment.transaction_id || payment.id);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(payment);
    return groups;
  }, new Map<string, any[]>());

  const futurePaymentsGrouped = futurePayments.reduce((groups, payment) => {
    const key = String(payment.transaction_id || payment.id);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(payment);
    return groups;
  }, new Map<string, any[]>());

  const pendingPaymentsGrouped = pendingPayments.reduce((groups, payment) => {
    const key = String(payment.transaction_id || payment.id);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(payment);
    return groups;
  }, new Map<string, any[]>());

  const orderedPaymentGroups = Array.from(paymentsGrouped.entries());
  const orderedFutureGroups = Array.from(futurePaymentsGrouped.entries());
  const orderedPendingGroups = Array.from(pendingPaymentsGrouped.entries());

  const salesTotal = accountPayments.reduce(
    (sum, payment) => sum + Number(payment.net_amount ?? payment.valor_liquido ?? payment.valor ?? 0),
    0,
  );
  
  const futureTotal = futurePayments.reduce(
    (sum, payment) => sum + Number(payment.net_amount ?? payment.valor_liquido ?? payment.valor ?? 0),
    0,
  );
  
  const pendingTotal = pendingPayments.reduce(
    (sum, payment) => sum + Number(payment.valor ?? 0),
    0,
  );

  const accountExpenses = currentAccount
    ? (expectedData?.transactions || []).filter(
        (t: any) => t.tipo === "saida" && t.bank_account_id === currentAccount.bank_account_id,
      )
    : [];

  const expensesTotal = accountExpenses.reduce(
    (sum, transaction) =>
      sum + Number(transaction.valor_liquido ?? transaction.valor ?? transaction.valor_total ?? 0),
    0,
  );

  // --- Resumo geral (todas as contas) ---
  const allPayments = (expectedData?.payments_raw || []) as Array<any>;
  const summaryLiquidated = allPayments.filter((p) => {
    const status = (p.status || 'pendente') as string;
    if (status !== 'confirmado') return false;
    const dataPrevista = p.data_prevista ? new Date(p.data_prevista) : null;
    const isLiquidated = !dataPrevista || dataPrevista <= closingDate;
    return isLiquidated;
  });
  const summaryFuture = allPayments.filter((p) => {
    const status = (p.status || 'pendente') as string;
    if (status !== 'confirmado') return false;
    const dataPrevista = p.data_prevista ? new Date(p.data_prevista) : null;
    const isLiquidated = !dataPrevista || dataPrevista <= closingDate;
    return !isLiquidated;
  });
  const summaryPending = allPayments.filter((p) => (p.status || 'pendente') === 'pendente');
  const summaryExpenses = ((expectedData?.transactions || []) as Array<any>).filter((t) => t.tipo === 'saida');

  const summaryLiquidatedTotal = summaryLiquidated.reduce((s, p) => s + Number(p.net_amount ?? p.valor_liquido ?? p.valor ?? 0), 0);
  const summaryFutureTotal = summaryFuture.reduce((s, p) => s + Number(p.net_amount ?? p.valor_liquido ?? p.valor ?? 0), 0);
  const summaryPendingTotal = summaryPending.reduce((s, p) => s + Number(p.valor ?? 0), 0);
  const summaryExpensesTotal = summaryExpenses.reduce((s, t) => s + Number(t.valor_liquido ?? t.valor ?? 0), 0);

  const handleNext = useCallback(() => setStepIndex((s) => Math.min(totalSteps - 1, s + 1)), [totalSteps]);
  const handlePrev = useCallback(() => setStepIndex((s) => Math.max(0, s - 1)), []);

  const closeSessionSchema = z
    .object({
      closing_notes: z.string().optional(),
    })
    .refine(
      () => {
        const hasDifferenceByAccount = accountRows.some(
          (r) => Math.abs((r.declared_amount || 0) - (r.expected_amount || 0)) > 0.5
        );
        return !hasDifferenceByAccount || true; // permitir fechar; validação dura pode ser adicionada depois
      },
      {
        message: "",
        path: ["closing_notes"],
      }
    );

  type CloseSessionForm = z.infer<typeof closeSessionSchema>;

  const form = useForm<CloseSessionForm>({
    resolver: zodResolver(closeSessionSchema),
    defaultValues: {
      closing_notes: "",
    },
  });

  useEffect(() => {
    // sem campo declarado no formulário; permanecemos apenas com notas
  }, [expectedData, accountRows, form]);

  const closeSessionMutation = useMutation({
    mutationFn: async (data: CloseSessionForm) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // 0. Vincular transações da janela à sessão (caso ainda estejam órfãs)
      try {
        const nowIso = new Date().toISOString();
        const { error: linkErr } = await supabase
          .from('transactions')
          .update({ cash_register_session_id: session.id } as any)
          .eq('tenant_id', session.tenant_id)
          .is('cash_register_session_id', null)
          .gte('created_at', session.opened_at)
          .lte('created_at', nowIso);
        if (linkErr) console.warn('Falha ao vincular transações à sessão no fechamento:', linkErr.message);
      } catch (e) {
        console.warn('Erro ao vincular transações à sessão no fechamento:', (e as any)?.message || e);
      }

      // 1. Persistir movimentos (consolidados conta × método) como phase='movement'
      if (breakdowns.length > 0) {
        const movementRows = breakdowns.map((b) => ({
          session_id: session.id,
          bank_account_id: b.bank_account_id,
          payment_method_id: b.payment_method_id,
          expected_amount: b.expected_amount,
          declared_amount: b.expected_amount,
          phase: 'movement',
        }));
        const { error: movementError } = await supabase
          .from("cash_register_breakdown")
          .upsert(movementRows as any, { onConflict: 'session_id,bank_account_id,payment_method_id,phase' });
        if (movementError) throw movementError;
      }

      // 2. Inserir fechamento por conta (phase='closing')
      if (accountRows.length > 0) {
        const closingRows = accountRows.map((r) => ({
          session_id: session.id,
          bank_account_id: r.bank_account_id,
          payment_method_id: null,
          expected_amount: Math.max(0, Number(r.expected_amount) || 0),
          declared_amount: Math.max(0, Number(r.declared_amount) || 0),
          notes: r.notes || null,
          phase: 'closing',
        }));
        const { error: closingError } = await supabase
          .from("cash_register_breakdown")
          .upsert(closingRows as any, { onConflict: 'session_id,bank_account_id,payment_method_id,phase' });
        if (closingError) throw closingError;
      }

      const declaredTotal = accountRows.reduce((sum, r) => sum + (r.declared_amount || 0), 0);
      const expectedTotal = accountRows.reduce((sum, r) => sum + (r.expected_amount || 0), 0);

      // 3. Fechar sessão
      const { error: sessionError } = await supabase
        .from("cash_register_sessions")
        .update({
          closed_at: new Date().toISOString(),
          closed_by: user.id,
          declared_balance: declaredTotal,
          expected_balance: expectedTotal,
          closing_notes: data.closing_notes || null,
          status: "closed",
        })
        .eq("id", session.id);

      if (sessionError) throw sessionError;
    },
    onSuccess: () => {
      toast.success("Caixa fechado com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["open-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["closed-sessions"] });
      // Atualiza janelas de sessões fechadas usadas para bloquear transações
      if (session?.tenant_id) {
        queryClient.invalidateQueries({ queryKey: ["closed-session-windows", session.tenant_id] });
      }
      // Atualiza listagem de transações e pagamentos
      queryClient.invalidateQueries({ queryKey: ["caixa-transactions"] });
      queryClient.invalidateQueries({ queryKey: ["transaction-payments"] });
      if (session?.id) {
        void logActivity({
          title: "Fechamento de caixa",
          description: String(new Date().toISOString()),
          categorySlug: "category:cash_register",
          entitySlug: "entity:cash_register_sessions",
          operationSlug: "operation:close",
          screenSlug: "caixa",
          metadata: { session_id: session.id },
        });
      }
      form.reset();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao fechar caixa");
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: CloseSessionForm) => {
    setIsSubmitting(true);
    closeSessionMutation.mutate(data);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // removido uso de declared_balance; somente contas são declaradas

  const updateAccountDeclared = (index: number, value: number) => {
    const rows = [...accountRows];
    rows[index].declared_amount = Math.max(0, Number(value) || 0);
    setAccountRows(rows);
  };

  const updateAccountNotes = (index: number, value: string) => {
    const rows = [...accountRows];
    rows[index].notes = value;
    setAccountRows(rows);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col min-h-0 overflow-hidden p-0">
        <DialogHeader className="px-6 py-4 border-b bg-background sticky top-0 z-10">
          <DialogTitle>Fechar Caixa</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          {/* Evitar submit implícito (Enter em inputs) */}
          <form onSubmit={(e) => e.preventDefault()} className="flex-1 flex min-h-0 flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 px-6 py-3">
          {/* Wizard - Intro */}
          {isIntro && (
            <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,280px)] md:items-start">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Vamos fechar o caixa?</h3>
                <p className="text-muted-foreground">Revise os valores por conta, confira as vendas e despesas e finalize o fechamento com segurança.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-muted rounded-md p-3">
                    <div className="text-xs text-muted-foreground">Abertura</div>
                    <div className="font-medium">{format(new Date(session.opened_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</div>
                  </div>
                  <div className="bg-muted rounded-md p-3">
                    <div className="text-xs text-muted-foreground">Saldo da Abertura</div>
                    <div className="font-semibold text-lg">{formatCurrency(session.opening_balance || 0)}</div>
                  </div>
                  <div className="bg-muted rounded-md p-3">
                    <div className="text-xs text-muted-foreground">Saldo Esperado até agora</div>
                    <div className="font-bold text-lg">{formatCurrency(expectedData?.expected_balance || 0)}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-base font-semibold">Saldos digitados na abertura desta sessão</h4>
                    <span className="text-xs text-muted-foreground">
                      Conferência realizada em {format(new Date(session.opened_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </span>
                  </div>
                  {loadingExpected ? (
                    <p className="text-xs text-muted-foreground">Carregando saldos de abertura...</p>
                  ) : accountRows && accountRows.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Conta</TableHead>
                          <TableHead className="text-right">Saldo da Abertura</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accountRows.map((row) => (
                          <TableRow key={row.bank_account_id}>
                            <TableCell className="py-1 text-sm">{row.bank_account_name}</TableCell>
                            <TableCell className="py-1 text-sm text-right">{formatCurrency(row.opening_amount || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-xs text-muted-foreground">Nenhum saldo de abertura foi encontrado para esta sessão.</p>
                  )}
                </div>
              </div>
              <div className="flex justify-center md:justify-end">
                <img
                  src="/img-financeiro-contas.jpg"
                  alt="Ilustração de gestão financeira durante conferência do caixa"
                  className="w-full max-w-[220px] md:max-w-[300px] max-h-[220px] object-contain rounded-md shadow-sm"
                />
              </div>
            </div>
          )}

          {/* Wizard - Por Conta (com vendas e despesas) */}
          {!isIntro && !isSummary && currentAccount && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg md:text-xl font-semibold">{currentAccount.bank_account_name}</h3>
                <p className="text-muted-foreground">Confira o saldo esperado, informe o saldo conferido e revise as movimentações desta conta.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground mb-3">Saldo da Abertura</div>
                  <div className="font-medium">{formatCurrency(currentAccount.opening_amount || 0)}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground mb-3">Abertura + Entradas + Saídas</div>
                  <div className="font-semibold">{formatCurrency(currentAccount.expected_amount || 0)}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Saldo na Conta</div>
                  <div className="mt-1">
                    <MoneyInput
                      value={currentAccount.declared_amount ?? 0}
                      onValueChange={(v) => {
                        const idx = accountRows.findIndex((r) => r.bank_account_id === currentAccount.bank_account_id);
                        if (idx >= 0) updateAccountDeclared(idx, v);
                      }}
                    />
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground mb-3">Diferença</div>
                  <div className={`font-medium ${Math.abs((currentAccount.declared_amount||0)-(currentAccount.expected_amount||0))>0.5 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {formatCurrency((currentAccount.declared_amount || 0) - (currentAccount.expected_amount || 0))}
                  </div>
                </div>
              </div>

              <Input
                type="text"
                placeholder="Comentário (opcional)"
                value={currentAccount.notes || ""}
                onChange={(e) => {
                  const idx = accountRows.findIndex((r) => r.bank_account_id === currentAccount.bank_account_id);
                  if (idx >= 0) updateAccountNotes(idx, e.target.value);
                }}
              />

              {/* Vendas (com pagamentos) e Despesas da conta */}
              <div className="space-y-4">
                {/* Grid: Pagamentos Liquidados (esquerda) e Despesas (direita) */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Pagamentos LIQUIDADOS (entram no fechamento) */}
                  <div className="rounded-lg border-2 border-green-200 bg-green-50/50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-green-900">✓ Pagamentos Liquidados</h4>
                        <p className="text-xs text-green-700">Estes valores JÁ CAÍRAM e entram no fechamento</p>
                      </div>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(salesTotal)}</span>
                    </div>
                  <div className="space-y-2">
                    {orderedPaymentGroups.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Nenhum pagamento liquidado nesta conta.</div>
                    ) : (
                      orderedPaymentGroups.map(([txId, items]) => {
                        const dt = items[0]?.created_at;
                        const total = items.reduce(
                          (sum: number, payment: any) =>
                            sum + Number(payment.net_amount ?? payment.valor_liquido ?? payment.valor ?? 0),
                          0,
                        );
                        return (
                          <div key={txId} className="rounded-md border bg-white p-2">
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-muted-foreground">
                                {new Intl.DateTimeFormat("pt-BR", {
                                  dateStyle: "short",
                                  timeStyle: "short",
                                  timeZone: "America/Sao_Paulo",
                                }).format(new Date(dt))}
                              </div>
                              <div className="font-semibold text-green-600">{formatCurrency(total)}</div>
                            </div>
                            <div className="mt-2 space-y-1">
                              {items.map((p: any, i: number) => {
                                const payVal = Number(p.net_amount ?? p.valor_liquido ?? p.valor ?? 0);
                                const hidePayVal = items.length === 1 && Math.abs(payVal - total) < 0.005;
                                return (
                                <div key={i} className="grid grid-cols-[1fr_auto] items-start text-xs gap-2">
                                  <div className="text-muted-foreground whitespace-normal break-words min-w-0 pr-2">
                                    {p.payment_method_name || "-"}
                                    {p.data_prevista && (
                                      <span className="ml-1 text-[10px]">
                                        (liquidou em {new Date(p.data_prevista).toLocaleDateString('pt-BR')})
                                      </span>
                                    )}
                                    {(p.client_name || p.product_names) && (
                                      <div className="text-[10px] text-muted-foreground/80 whitespace-normal break-words min-w-0">
                                        {p.client_name}{p.product_names ? ` • ${p.product_names}` : ""}
                                      </div>
                                    )}
                                  </div>
                                  <div className="font-medium text-right">{hidePayVal ? '' : formatCurrency(payVal)}</div>
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  </div>

                  {/* Despesas (lado direito) */}
                  <div className="rounded-lg border-2 border-red-200 bg-red-50/50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-red-900">Despesas</h4>
                        <p className="text-xs text-red-700">Descontam no dia (sempre instantâneas)</p>
                      </div>
                      <span className="text-sm font-bold text-red-600">{formatCurrency(expensesTotal)}</span>
                    </div>
                    <div className="space-y-2">
                      {accountExpenses.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Sem despesas nesta conta.</div>
                      ) : (
                        accountExpenses.map((t: any, i: number) => (
                          <div key={i} className="flex flex-wrap items-center justify-between rounded-md border bg-white p-2 text-xs gap-2">
                            <div className="text-muted-foreground whitespace-normal break-words min-w-0 pr-2 grow">
                              {new Intl.DateTimeFormat("pt-BR", {
                                dateStyle: "short",
                                timeStyle: "short",
                                timeZone: "America/Sao_Paulo",
                              }).format(new Date(t.created_at))}
                              {(t.categoria || t.descricao) && (
                                <div className="text-[10px] text-muted-foreground/80 mt-0.5 whitespace-normal break-words">
                                  {t.categoria || "-"}{t.descricao ? ` • ${t.descricao}` : ""}
                                </div>
                              )}
                            </div>
                            <div className="font-medium text-red-600 shrink-0 text-right">
                              {formatCurrency(Number(t.valor_liquido ?? t.valor ?? t.valor_total ?? 0))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Pagamentos FUTUROS e PENDENTES lado a lado */}
                <div className="grid gap-4 md:grid-cols-2">
                  {orderedFutureGroups.length > 0 && (
                    <div className="rounded-lg border-2 border-orange-200 bg-orange-50/50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-orange-900">⏳ Pagamentos Futuros</h4>
                          <p className="text-xs text-orange-700">Estes valores NÃO CAÍRAM ainda (crédito/débito)</p>
                        </div>
                        <span className="text-sm font-bold text-orange-600">{formatCurrency(futureTotal)}</span>
                      </div>
                      <div className="space-y-2">
                        {orderedFutureGroups.map(([txId, items]) => {
                          const dt = items[0]?.created_at;
                          const total = items.reduce(
                            (sum: number, payment: any) =>
                              sum + Number(payment.net_amount ?? payment.valor_liquido ?? payment.valor ?? 0),
                            0,
                          );
                          return (
                            <div key={txId} className="rounded-md border bg-white p-2">
                              <div className="flex items-center justify-between">
                                <div className="text-xs text-muted-foreground">
                                  {new Intl.DateTimeFormat("pt-BR", {
                                    dateStyle: "short",
                                    timeStyle: "short",
                                    timeZone: "America/Sao_Paulo",
                                  }).format(new Date(dt))}
                                </div>
                                <div className="font-semibold text-orange-600">{formatCurrency(total)}</div>
                              </div>
                              <div className="mt-2 space-y-1">
                                {items.map((p: any, i: number) => {
                                  const payVal = Number(p.net_amount ?? p.valor_liquido ?? p.valor ?? 0);
                                  const hidePayVal = items.length === 1 && Math.abs(payVal - total) < 0.005;
                                  return (
                                    <div key={i} className="flex flex-wrap items-center justify-between text-xs gap-2">
                                      <div className="text-muted-foreground whitespace-normal break-words min-w-0 pr-2 grow">
                                        {p.payment_method_name || "-"}
                                        {p.data_prevista && (
                                          <span className="ml-1 text-[10px] text-orange-600 font-medium">
                                            (previsto: {new Date(p.data_prevista).toLocaleDateString('pt-BR')})
                                          </span>
                                        )}
                                        {(p.client_name || p.product_names) && (
                                          <div className="text-[10px] text-muted-foreground/80 whitespace-normal break-words min-w-0 pr-2">
                                            {p.client_name}{p.product_names ? ` • ${p.product_names}` : ""}
                                          </div>
                                        )}
                                      </div>
                                      <div className="font-medium shrink-0 text-right">{hidePayVal ? '' : formatCurrency(payVal)}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {orderedPendingGroups.length > 0 && (
                    <div className="rounded-lg border-2 border-yellow-200 bg-yellow-50/50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-yellow-900">⚠ Pagamentos Pendentes</h4>
                          <p className="text-xs text-yellow-700">Aguardando confirmação</p>
                        </div>
                        <span className="text-sm font-bold text-yellow-600">{formatCurrency(pendingTotal)}</span>
                      </div>
                      <div className="space-y-2">
                        {orderedPendingGroups.map(([txId, items]) => {
                          const dt = items[0]?.created_at;
                          const total = items.reduce(
                            (sum: number, payment: any) =>
                              sum + Number(payment.valor ?? 0),
                            0,
                          );
                          return (
                            <div key={txId} className="rounded-md border bg-white p-2">
                              <div className="flex items-center justify-between">
                                <div className="text-xs text-muted-foreground">
                                  {new Intl.DateTimeFormat("pt-BR", {
                                    dateStyle: "short",
                                    timeStyle: "short",
                                    timeZone: "America/Sao_Paulo",
                                  }).format(new Date(dt))}
                                </div>
                                <div className="font-semibold text-yellow-600">{formatCurrency(total)}</div>
                              </div>
                              <div className="mt-2 space-y-1">
                                {items.map((p: any, i: number) => {
                                  const payVal = Number(p.valor ?? 0);
                                  const hidePayVal = items.length === 1 && Math.abs(payVal - total) < 0.005;
                                  return (
                                    <div key={i} className="flex flex-wrap items-center justify-between text-xs gap-2">
                                      <div className="text-muted-foreground whitespace-normal break-words min-w-0 pr-2 grow">
                                        {p.payment_method_name || "-"}
                                        <div className="text-[10px] text-muted-foreground/80 whitespace-normal break-words min-w-0 pr-2">
                                          {(p.client_name || p.product_names) && (
                                            <>
                                              {p.client_name}{p.product_names ? ` • ${p.product_names}` : ""}
                                              {" "}
                                            </>
                                          )}
                                          {p.payment_id && (
                                            <span className="ml-1">#{String(p.payment_id).slice(-6)}</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="font-medium shrink-0 text-right">{hidePayVal ? '' : formatCurrency(payVal)}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Wizard - Resumo final */}
          {isSummary && (
            <>
              <div>
                <h3 className="font-semibold">Conferência de saldo em Conta</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Conta</TableHead>
                      <TableHead className="text-right">
                        Saldo na<br />Abertura
                      </TableHead>
                      <TableHead className="text-right">
                        Soma no Sistema
                        <div className="text-[10px] text-muted-foreground/80">
                          Abertura + Entradas + Saídas
                        </div>
                      </TableHead>
                      <TableHead className="text-right">
                        Saldo Conferido
                        <div className="text-[10px] text-muted-foreground/80">
                          Conferencia Caixa e Contas
                        </div>
                      </TableHead>
                      <TableHead className="text-right">Diferença</TableHead>
                      <TableHead>Observação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accountRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="py-2 text-center text-sm text-muted-foreground">
                          Nenhuma conta encontrada para esta sessão.
                        </TableCell>
                      </TableRow>
                    ) : (
                      accountRows.map((row, index) => {
                        const difference = (row.declared_amount || 0) - (row.expected_amount || 0);
                        const hasDifference = Math.abs(difference) > 0.5;
                        return (
                          <TableRow key={row.bank_account_id} className="">
                            <TableCell className="py-1">{row.bank_account_name}</TableCell>
                            <TableCell className="text-right py-1">{formatCurrency(row.opening_amount || 0)}</TableCell>
                            <TableCell className="text-right py-1">{formatCurrency(row.expected_amount)}</TableCell>
                            <TableCell className="text-right py-1">{formatCurrency(row.declared_amount ?? 0)}</TableCell>
                            <TableCell className={`text-right font-medium py-1 ${hasDifference ? "text-destructive" : "text-muted-foreground"}`}>
                              {formatCurrency(difference)}
                            </TableCell>
                            <TableCell className="py-1">{row.notes || '-'}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Observações Gerais (opcional) imediatamente após a conferência por conta */}
              <FormField
                control={form.control}
                name="closing_notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Observações Gerais (opcional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Adicione observações sobre o fechamento do caixa..."
                        disabled={isSubmitting}
                        {...field}
                        className="min-h-[50px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Quadros agregados (todas as contas): Liquidados/Despesas no topo e Futuros/Pendentes abaixo */}
              <div className="grid gap-4 md:grid-cols-2">
                {/* Pagamentos LIQUIDADOS (entram no fechamento) */}
                <div className="rounded-lg border-2 border-green-200 bg-green-50/50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Pagamentos Liquidados</h4>
                      <p className="text-xs text-muted-foreground">Entram no fechamento (todas as contas)</p>
                    </div>
                    <div className="font-bold text-green-700">{formatCurrency(summaryLiquidatedTotal)}</div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {summaryLiquidated.length === 0 ? (
                      <div className="text-xs text-muted-foreground">Sem pagamentos liquidados.</div>
                    ) : (
                      summaryLiquidated.map((p: any, i: number) => (
                        <div key={`sum-liq-${i}`} className="flex items-start justify-between text-xs gap-2">
                          <div className="text-muted-foreground">
                            <div className="font-medium text-foreground">
                              {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(p.created_at))}
                            </div>
                            {p.payment_method_name || "-"}
                            {p.bank_account_name && (
                              <span className="ml-1 text-[10px]">({p.bank_account_name})</span>
                            )}
                            {p.data_prevista && (
                              <span className="ml-1 text-[10px]">(liquidou em {new Date(p.data_prevista).toLocaleDateString('pt-BR')})</span>
                            )}
                            {(p.client_name || p.product_names) && (
                              <div className="text-[10px] text-muted-foreground/80 whitespace-normal break-words min-w-0 pr-2">
                                {p.client_name}{p.product_names ? ` • ${p.product_names}` : ""}
                              </div>
                            )}
                          </div>
                          <div className="font-medium">{formatCurrency(Number(p.net_amount ?? p.valor_liquido ?? p.valor ?? 0))}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Despesas (lado direito) */}
                <div className="rounded-lg border-2 border-red-200 bg-red-50/50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Despesas</h4>
                      <p className="text-xs text-muted-foreground">Descontam no dia (sempre instantâneas)</p>
                    </div>
                    <div className="font-bold text-red-700">{formatCurrency(summaryExpensesTotal)}</div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {summaryExpenses.length === 0 ? (
                      <div className="text-xs text-muted-foreground">Sem despesas.</div>
                    ) : (
                      summaryExpenses.map((t: any, i: number) => (
                        <div key={`sum-exp-${i}`} className="flex items-start justify-between text-xs gap-2">
                          <div className="text-muted-foreground">
                            <div className="font-medium text-foreground">
                              {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(t.created_at))}
                            </div>
                            {t.payment_method_name || "-"}
                            {t.bank_account_name && (
                              <span className="ml-1 text-[10px]">({t.bank_account_name})</span>
                            )}
                            {(t.categoria || t.descricao) && (
                              <div className="text-[10px] text-muted-foreground/80 whitespace-normal break-words min-w-0 pr-2">
                                {t.categoria || "-"}{t.descricao ? ` • ${t.descricao}` : ""}
                              </div>
                            )}
                          </div>
                          <div className="font-medium">{formatCurrency(Number(t.valor_liquido ?? 0))}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Abaixo: Futuros e Pendentes lado a lado */}
              <div className="grid gap-4 md:grid-cols-2">
                {/* Futuros */}
                <div className="rounded-lg border-2 border-orange-200 bg-orange-50/40 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Pagamentos Futuros</h4>
                      <p className="text-xs text-muted-foreground">Não entram no fechamento de hoje</p>
                    </div>
                    <div className="font-bold text-orange-700">{formatCurrency(summaryFutureTotal)}</div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {summaryFuture.length === 0 ? (
                      <div className="text-xs text-muted-foreground">Sem pagamentos futuros.</div>
                    ) : (
                      summaryFuture.map((p: any, i: number) => (
                        <div key={`sum-fut-${i}`} className="flex items-start justify-between text-xs gap-2">
                          <div className="text-muted-foreground">
                            <div className="font-medium text-foreground">
                              {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(p.created_at))}
                            </div>
                            {p.payment_method_name || "-"}
                            {p.bank_account_name && (
                              <span className="ml-1 text-[10px]">({p.bank_account_name})</span>
                            )}
                            {p.data_prevista && (
                              <span className="ml-1 text-[10px]">(previsto para {new Date(p.data_prevista).toLocaleDateString('pt-BR')})</span>
                            )}
                            {(p.client_name || p.product_names) && (
                              <div className="text-[10px] text-muted-foreground/80 whitespace-normal break-words min-w-0 pr-2">
                                {p.client_name}{p.product_names ? ` • ${p.product_names}` : ""}
                              </div>
                            )}
                          </div>
                          <div className="font-medium">{formatCurrency(Number(p.net_amount ?? p.valor_liquido ?? p.valor ?? 0))}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Pendentes */}
                <div className="rounded-lg border-2 border-yellow-200 bg-yellow-50/40 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Pagamentos Pendentes</h4>
                      <p className="text-xs text-muted-foreground">Aguardando confirmação</p>
                    </div>
                    <div className="font-bold text-yellow-700">{formatCurrency(summaryPendingTotal)}</div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {summaryPending.length === 0 ? (
                      <div className="text-xs text-muted-foreground">Sem pagamentos pendentes.</div>
                    ) : (
                      summaryPending.map((p: any, i: number) => (
                        <div key={`sum-pend-${i}`} className="flex items-start justify-between text-xs gap-2">
                          <div className="text-muted-foreground">
                            <div className="font-medium text-foreground">
                              {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(p.created_at))}
                            </div>
                            {p.payment_method_name || "-"}
                            {p.bank_account_name && (
                              <span className="ml-1 text-[10px]">({p.bank_account_name})</span>
                            )}
                            {(p.client_name || p.product_names) && (
                              <div className="text-[10px] text-muted-foreground/80 whitespace-normal break-words min-w-0 pr-2">
                                {p.client_name}{p.product_names ? ` • ${p.product_names}` : ""}
                              </div>
                            )}
                            {p.id && (
                              <span className="ml-1 text-[10px]">#{String(p.id).slice(-6)}</span>
                            )}
                          </div>
                          <div className="font-medium">{formatCurrency(Number(p.valor ?? 0))}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              
            </>
          )}

          {/* Grid inferior (mantido no resumo na nova estrutura) */}
          {false && <div className="grid gap-6 md:grid-cols-2">
            {/* Resumo da Sessão (esquerda) */}
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Abertura:</span>
                <span className="font-medium">
                  {format(new Date(session.opened_at), "dd/MM/yyyy 'às' HH:mm", {
                    locale: ptBR,
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Saldo Inicial:</span>
                <span className="font-medium text-lg">
                  {formatCurrency(session.opening_balance)}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-muted-foreground">Saldo Esperado:</span>
                <span className="font-bold text-lg">
                  {formatCurrency(expectedData?.expected_balance || 0)}
                </span>
              </div>
            </div>

            {/* Campos (direita) */}
            <div className="space-y-2">

              {/* Vendas no Cartão (não entram no saldo) */}
              {expectedData?.credit_by_account && expectedData.credit_by_account.length > 0 && (
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Conta</TableHead>
                        <TableHead className="text-right">Total no crédito</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(expectedData.credit_by_account as any[]).map((row) => (
                        <TableRow key={row.bank_account_id}>
                          <TableCell className="py-1">{row.bank_account_name}</TableCell>
                          <TableCell className="py-1 text-right">{formatCurrency(row.total || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              <FormField
                control={form.control}
                name="closing_notes"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        placeholder="Adicione observações sobre o fechamento do caixa..."
                        disabled={isSubmitting}
                        {...field}
                        className="min-h-[50px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>}

          {/* Lista compacta original (mantida no resumo) */}
          {false && expectedData?.transactions && expectedData.transactions.length > 0 && (
            <div>
              <h3 className="font-semibold">Transações da Sessão</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Conta</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(expectedData.transactions as any[]).map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="py-1 text-sm">
                        {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(t.created_at))}
                      </TableCell>
                      <TableCell className="py-1 text-sm">{t.bank_account_name || "-"}</TableCell>
                      <TableCell className="py-1 text-sm">{t.payment_method_name || "-"}</TableCell>
                      <TableCell className="py-1 text-sm capitalize">{t.tipo}</TableCell>
                      <TableCell className={`py-1 text-sm text-right ${t.tipo === 'entrada' ? 'text-green-600' : t.tipo === 'saida' ? 'text-red-600' : ''}`}>
                        {formatCurrency(t.valor_liquido)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(() => {
                    const txs = expectedData.transactions as any[];
                    const onlyConfirmed = (t:any) => (t.status || 'pendente') === 'confirmado';
                    const isInstant = (t:any) => t.is_instant === true;

                    const inRows = txs.filter(t => t.tipo === 'entrada' && onlyConfirmed(t));
                    const outRows = txs.filter(t => t.tipo === 'saida' && onlyConfirmed(t));

                    const inAccount = inRows.filter(isInstant).reduce((s, t) => s + (t.valor_liquido || 0), 0);
                    const inCredit  = inRows.filter(t => !isInstant(t)).reduce((s, t) => s + (t.valor_liquido || 0), 0);
                    const outAccount = outRows.filter(isInstant).reduce((s, t) => s + (t.valor_liquido || 0), 0);
                    const outCredit  = outRows.filter(t => !isInstant(t)).reduce((s, t) => s + (t.valor_liquido || 0), 0);

                    const netAccount = inAccount - outAccount;
                    const netCredit  = inCredit - outCredit;

                    return (
                      <>
                        <TableRow>
                          <TableCell colSpan={5} className="p-0">
                            <div className="h-0 border-b border-border border-gray-400" />
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-b border-border">
                          <TableCell className="py-1 text-xs text-muted-foreground" colSpan={3}>Subtotal de Entradas</TableCell>
                          <TableCell className="py-1 text-xs text-muted-foreground text-right">Em conta: <span className="text-green-600 font-semibold">{formatCurrency(inAccount)}</span></TableCell>
                          <TableCell className="py-1 text-xs text-muted-foreground text-right">Crédito: <span className="text-green-600 font-semibold">{formatCurrency(inCredit)}</span></TableCell>
                        </TableRow>
                        <TableRow className="">
                          <TableCell className="py-1 text-xs text-muted-foreground" colSpan={3}>Subtotal de Saídas</TableCell>
                          <TableCell className="py-1 text-xs text-muted-foreground text-right">Em conta: <span className="text-red-600 font-semibold">{formatCurrency(outAccount)}</span></TableCell>
                          <TableCell className="py-1 text-xs text-muted-foreground text-right">Crédito: <span className="text-red-600 font-semibold">{formatCurrency(outCredit)}</span></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={5} className="p-0">
                            <div className="h-0 border-b border-border border-gray-300" />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="py-1 text-sm font-semibold" colSpan={3}>Total Líquido (Entradas - Saídas)</TableCell>
                          <TableCell className={`py-1 text-sm text-right font-bold ${netAccount >= 0 ? 'text-green-700' : 'text-red-700'}`}>Em conta: {formatCurrency(netAccount)}</TableCell>
                          <TableCell className={`py-1 text-sm text-right font-bold ${netCredit >= 0 ? 'text-green-700' : 'text-red-700'}`}>Crédito: {formatCurrency(netCredit)}</TableCell>
                        </TableRow>
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
          )}
            </div>

            {/* Footer fixo */}
            <div className="flex justify-between gap-2 px-6 py-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <div className="flex gap-2">
                {stepIndex > 0 && (
                  <Button type="button" variant="outline" onClick={handlePrev} disabled={isSubmitting}>
                    Voltar
                  </Button>
                )}
                {!isSummary ? (
                  <Button type="button" onClick={handleNext} disabled={isSubmitting}>
                    Avançar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={() => form.handleSubmit(onSubmit)()}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Fechando..." : "Finalizar Fechamento de Caixa"}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
